<script setup lang="ts">
import { propsFactory } from "@soft-db/soft-utils";
import { computed, type PropType } from "vue";
import type { CursorOptions, XYCursorOptions } from "../interfaces";
import { useCartesianChart } from "../use/cartesian-chart";
import { useSVGContainer } from "../use/svg-container";

const props = defineProps(
	propsFactory<XYCursorOptions>()({
		x: Object as PropType<CursorOptions>,
		y: Object as PropType<CursorOptions>,
	})
);

type PathCursor = {
	id: string;
	d: string;
	class?: string;
};

const { mouse, contentDimension } = useSVGContainer();

const xLine = (x: number) => {
	const { height } = contentDimension.value;
	return `M${x},${height} ${x}, 0`;
};

const yLine = (y: number) => {
	const { width } = contentDimension.value;
	return `M${width},${y} 0,${y}`;
};

const xLiveCursor = computed<PathCursor | undefined>(() => {
	if (!props.x?.live) return;

	const { x } = mouse.position.value;

	return {
		id: "x-live-cursor",
		d: xLine(x),
	};
});

const yLiveCursor = computed<PathCursor | undefined>(() => {
	if (!props.y?.live) return;

	const { y } = mouse.position.value;

	return {
		id: "y-live-cursor",
		d: yLine(y),
	};
});

const { scales } = useCartesianChart();

const xStaticCursor = computed(() => {
	if (!props.x?.value) return;

	const scaleSide = props.x.scale || "bottom";
	const scale = scales.value[scaleSide];
	if (!scale) return;

	const x = scale(props.x.value);
	return {
		id: "x-static-cursor",
		d: xLine(x),
	};
});

const yStaticCursor = computed(() => {
	if (!props.y?.value) return;

	const scaleSide = props.y.scale || "left";
	const scale = scales.value[scaleSide];
	if (!scale) return;

	const y = scale(props.y.value);
	return {
		id: "x-static-cursor",
		d: yLine(y),
	};
});

const liveCursors = computed<PathCursor[]>(() => {
	const cursors = [xLiveCursor.value, yLiveCursor.value];
	return cursors.filter((c): c is PathCursor => !!c);
});

const staticCursors = computed<PathCursor[]>(() => {
	const cursors = [xStaticCursor.value, yStaticCursor.value];
	return cursors.filter((c): c is PathCursor => !!c);
});
</script>

<template>
	<g>
		<path v-for="cursor of staticCursors" :key="cursor.id" class="xy-cursor" :d="cursor.d" />
	</g>
	<g v-if="mouse.inBounds.value">
		<path v-for="cursor of liveCursors" :key="cursor.id" class="xy-cursor live" :d="cursor.d" />
	</g>
</template>

<style scoped>
.xy-cursor {
	opacity: 100%;
	stroke: var(--color-black);
	stroke-width: 1;

	&:where([data-theme="dark"] *) {
		stroke: var(--color-white);
	}
}

.xy-cursor.live {
	opacity: 60%;
	stroke-dasharray: 8 4;
}
</style>
